/**
 * TypeScript type definitions for KYC Main Service API
 */

// Authentication types
export interface LoginRequest {
  username: string;
  password: string;
  device_info?: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  access_token_expires_in: number;
  token_type: string;
  user_id: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RegisterRequest {
  username: string;
  password: string;
  email: string;
  first_name: string;
  last_name: string;
}

// Common types
export class ApiError extends Error {
  status?: number;
  code?: string;
  details?: unknown;

  constructor(message: string, status?: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export interface HealthCheckResponse {
  status: string;
  service: string;
  version: string;
}

export interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
}

// User types
export interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  roles?: string[];
  adminRole?: string;
  status?: string;
  created_at: string;
  updated_at: string;
  address?: string;
  phone?: string;
  passwordLastChanged?: string;
  failedLoginAttempts?: number;
  apiAccess?: boolean;
  activityLog?: Array<{
    id: string;
    action: string;
    description: string;
    timestamp: string;
    ipAddress: string;
  }>;
  documents?: Array<{
    id: string;
    type: string;
    uploadedAt: string;
    status: string;
  }>;
  kycStatus?: string;
  twoFactorEnabled?: boolean;
}

export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  role: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface AdminUserStatsResponse {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  deactivated: number;
  pending_activation: number;
}

export interface AdminBulkUpdateResponse {
  results: Array<{
    user_id: string;
    success: boolean;
    error?: string;
  }>;
  total_processed: number;
  success_count: number;
  error_count: number;
}

export interface AdminUserCreateRequest {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  role: string;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface KYCVerification {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  document_type: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface TrainingParameters {
  learning_rate: number;
  batch_size: number;
  epochs: number;
  validation_split: number;
  optimizer: string;
  loss_function: string;
  early_stopping: boolean;
  patience: number;
  custom_parameters?: Record<string, unknown>;
}

export interface TrainingProgress {
  current_epoch: number;
  total_epochs: number;
  current_step: number;
  total_steps: number;
  progress_percentage: number;
  current_loss: number;
  current_accuracy: number;
  validation_loss?: number;
  validation_accuracy?: number;
  estimated_completion?: string;
  stage: 'data_preparation' | 'training' | 'validation' | 'finalizing';
}

export interface TrainingMetrics {
  final_loss: number;
  final_accuracy: number;
  validation_loss: number;
  validation_accuracy: number;
  training_time_seconds: number;
  model_size_mb: number;
  convergence_epoch: number;
  best_epoch: number;
  learning_curve?: Array<{
    epoch: number;
    loss: number;
    accuracy: number;
  }>;
}

export interface TrainingBatch {
  id: string;
  name: string;
  description: string;
  status:
    | 'pending'
    | 'preparing'
    | 'training'
    | 'validating'
    | 'completed'
    | 'failed'
    | 'cancelled';
  model_type: string;
  dataset_ids: string[];
  parameters: TrainingParameters;
  progress: TrainingProgress;
  created_by: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  metrics?: TrainingMetrics;
}

export interface TrainingBatchListResponse {
  batches: TrainingBatch[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface TrainingBatchCreateRequest {
  name: string;
  description: string;
  model_type: string;
  dataset_ids: string[];
  parameters: TrainingParameters;
}

export interface TrainingBatchUpdateRequest {
  name?: string;
  description?: string;
  status?: string;
  parameters?: Partial<TrainingParameters>;
}

export interface TrainingJobTriggerRequest {
  batch_id: string;
  priority?: 'low' | 'normal' | 'high';
  notify_on_completion?: boolean;
  custom_config?: Record<string, unknown>;
}

export interface TrainingJobResponse {
  job_id: string;
  batch_id: string;
  status: string;
  estimated_duration_minutes: number;
  queue_position: number;
  created_at: string;
}

export interface TrainingStatistics {
  total_batches: number;
  active_batches: number;
  completed_batches: number;
  failed_batches: number;
  average_training_time_minutes: number;
  success_rate: number;
  models_trained_24h: number;
  models_trained_7d: number;
  current_queue_size: number;
  estimated_wait_time_minutes: number;
}

export interface AdminUserUpdateRequest {
  email: string;
  first_name: string;
  last_name: string;
}

export interface AdminUserStatusUpdateRequest {
  status: string;
  reason?: string;
}

export interface AdminBulkStatusUpdateRequest {
  user_ids: string[];
  status: string;
  reason?: string;
}

export interface CreateRoleRequest {
  name: string;
  description: string;
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
}

// KYC types
export interface KYCApplication {
  user_id: string;
  document_type: string;
}

export interface KYCStatus {
  user_id: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface KYCVerification {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  document_type: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateKYCStatusRequest {
  status: 'pending' | 'approved' | 'rejected';
  notes?: string;
}

// Camera Management Types
export interface Camera {
  id: string;
  name: string;
  description: string;
  type: string;
  device_type?: string;
  protocol: string;
  ip_address: string;
  port: number;
  username: string;
  rtsp_url?: string;
  status: string;
  zone_id: string;
  site_id?: string;
  agent_id?: string;
  manufacturer?: string;
  model?: string;
  parent_device_id?: string;
  channel_number?: number;
  /** The admin API sends a string; `CameraLocation` is kept for older callers. Render it with formatCameraLocation(). */
  location?: string | CameraLocation;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_seen_at?: string;
  created_by: string;
  updated_by: string;
}

export interface CameraLocation {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  address: string;
  floor?: string;
  room?: string;
  created_at: string;
  updated_at: string;
}

export interface CameraListResponse {
  cameras: Camera[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface CameraStatsResponse {
  total_cameras: number;
  active_cameras: number;
  inactive_cameras: number;
  online_cameras: number;
  offline_cameras: number;
  error_cameras: number;
  cameras_by_zone: Record<string, number>;
  cameras_by_type: Record<string, number>;
  total_events_24h: number;
  error_events_24h: number;
}

export interface CameraCreateRequest {
  name: string;
  description: string;
  type: string;
  protocol: string;
  ip_address: string;
  port: number;
  username?: string;
  password?: string;
  zone_id: string;
}

export interface CameraUpdateRequest {
  name: string;
  description: string;
  type: string;
  protocol: string;
  ip_address: string;
  port: number;
  username?: string;
  password?: string;
  zone_id: string;
  is_active: boolean;
}

export interface CameraStatusUpdateRequest {
  status: string;
  reason?: string;
}

/**
 * Physical location detail for GET/PUT /api/v1/admin/cameras/:id/location.
 * The PUT accepts a partial object — only the sent fields are overwritten.
 */
export interface CameraLocationDetail {
  camera_id?: string;
  building?: string;
  floor?: string;
  room?: string;
  latitude?: number;
  longitude?: number;
  notes?: string;
}

export type CameraLocationUpdateRequest = Omit<
  CameraLocationDetail,
  'camera_id'
>;

/** Body accepted by PUT /api/v1/admin/cameras/:id/configuration. */
export interface CameraConfigurationUpdateRequest {
  enable_face_detection: boolean;
}

export interface CameraBulkStatusUpdateRequest {
  camera_ids: string[];
  status: string;
  reason?: string;
}

export interface CameraBulkUpdateResponse {
  results: Array<{
    camera_id: string;
    success: boolean;
    error?: string;
  }>;
  total_processed: number;
  success_count: number;
  error_count: number;
}

export interface CameraCredentialsRolloutRequest {
  source_camera_id: string;
  target_camera_ids?: string[];
  zone_id?: string;
  status?: string;
}

export interface CameraCredentialsRolloutResponse {
  message: string;
  updated_count: number;
  updated_by: string;
}

export interface DiscoveredDevice {
  id: string;
  device_id?: string; // API sometimes returns this
  tenant_id?: string;
  site_id?: string;
  agent_id?: string; // API returns agent_id
  agent_device_id?: string;
  hardware_id: string;
  serial_number?: string;
  ip: string;
  port: number;
  xaddr?: string;
  source?: string;
  open_ports?: number[];
  rtsp_support: boolean;
  device_type?: string; // camera, nvr, dvr, anpr, encoder, etc.
  manufacturer?: string;
  model?: string;
  firmware?: string;
  first_seen: string;
  last_seen: string;
  last_scan_id?: string;
  last_scan_at?: string;
  failed_probes_count?: number;
  adopted?: boolean;
  channel_count?: number;
  // Short ID for easy identification (e.g., "nvr022" for NVR at 172.168.0.22)
  short_id?: string;
  // Timezone detection fields
  timezone_offset?: number; // Offset in minutes from UTC
  timezone_source?: string; // "onvif", "ntp", "manual"
  device_time?: string; // Device's reported time (ISO)
  system_time_delta?: number; // Diff in seconds from agent time
  timezone_checked_at?: string; // When timezone was last checked (ISO)
}

export interface DiscoveredDeviceListResponse {
  devices: DiscoveredDevice[];
  total: number;
  limit: number;
  offset: number;
}

// NVR Channel Information
export interface NVRChannelInfo {
  channel_number: number;
  name: string;
  camera_id?: string;
  camera_name?: string;
  status: 'live' | 'error' | 'not_connected';
  rtsp_url: string;
  thumbnail_url?: string;
  has_playback: boolean;
  last_seen?: string;
  manufacturer?: string;
  model?: string;
}

export interface NVRChannelListResponse {
  device_id: string;
  channel_count: number;
  channels: NVRChannelInfo[];
}

export interface ServiceStatus {
  name: string;
  status: string;
  uptime_seconds: number;
  version: string;
  last_check: string;
}

export interface DatabaseStatus {
  status: string;
  active_connections: number;
  avg_query_time_ms: number;
  size_bytes: number;
  last_backup: string;
}

export interface StorageStatus {
  total_space_bytes: number;
  used_space_bytes: number;
  free_space_bytes: number;
  usage_percent: number;
}

export interface NetworkStatus {
  latency_ms: number;
  bandwidth_bps: number;
  packet_loss_percent: number;
}

export interface SystemOverview {
  services: ServiceStatus[];
  database: DatabaseStatus;
  storage: StorageStatus;
  network: NetworkStatus;
}

export interface DashboardStats {
  total_users: number;
  active_users: number;
  pending_kyc: number;
  total_cameras: number;
  active_cameras: number;
  online_cameras: number;
  system_health: string;
  last_updated: string;
  uptime_seconds: number;
  error_rate: number;
  avg_response_time_ms: number;
}

export interface UserAnalyticsFilters {
  start_date?: string;
  end_date?: string;
  status?: string;
  role?: string;
  group_by?: string;
}

export interface UserGrowthData {
  date: string;
  new_users: number;
  total_users: number;
}

export interface UserActivityData {
  date: string;
  active_users: number;
  sessions: number;
  page_views: number;
  avg_session_time_minutes: number;
}

export interface GeographicData {
  country: string;
  region: string;
  user_count: number;
}

export interface DeviceData {
  device_type: string;
  browser: string;
  os: string;
  user_count: number;
}

export interface UserAnalytics {
  total_users: number;
  active_users: number;
  new_users: number;
  inactive_users: number;
  user_growth: UserGrowthData[];
  user_activity: UserActivityData[];
  geographic_data: GeographicData[];
  device_data: DeviceData[];
}

export interface ActivityMetricsFilters {
  start_date?: string;
  end_date?: string;
  user_id?: string;
  activity_type?: string;
}

export interface ActivityTypeData {
  type: string;
  count: number;
  percentage: number;
}

export interface HourlyActivityData {
  hour: number;
  count: number;
}

export interface TopUserData {
  user_id: string;
  username: string;
  activity_count: number;
}

export interface ActivityMetrics {
  total_activities: number;
  unique_users: number;
  activity_types: ActivityTypeData[];
  hourly_activity: HourlyActivityData[];
  top_users: TopUserData[];
}

export interface RegistrationData {
  date: string;
  registrations: number;
  cumulative_total: number;
}

export interface RegistrationTrends {
  period: string;
  trends: RegistrationData[];
  growth_rate: number;
  total_registrations: number;
}

export interface KYCAnalyticsFilters {
  start_date?: string;
  end_date?: string;
  status?: string;
  document_type?: string;
}

export interface ProcessingTimeData {
  average_seconds: number;
  median_seconds: number;
  min_seconds: number;
  max_seconds: number;
  p95_seconds: number;
}

export interface DocumentTypeData {
  type: string;
  count: number;
  approval_rate: number;
}

export interface DailyKYCData {
  date: string;
  total_requests: number;
  approved: number;
  rejected: number;
  pending: number;
}

export interface KYCAnalytics {
  total_requests: number;
  approved_requests: number;
  rejected_requests: number;
  pending_requests: number;
  approval_rate: number;
  processing_time: ProcessingTimeData;
  document_types: DocumentTypeData[];
  daily_stats: DailyKYCData[];
}

export interface KYCStatusDistribution {
  pending: number;
  approved: number;
  rejected: number;
  failed: number;
  total: number;
  percentages: Record<string, number>;
}

export interface ProcessingTimeTrend {
  date: string;
  average_time_seconds: number;
  request_count: number;
}

export interface ProcessingTimeFilters {
  start_date?: string;
  end_date?: string;
  status?: string;
}

export interface ProcessingTimeMetrics {
  overall: ProcessingTimeData;
  by_status: Record<string, ProcessingTimeData>;
  by_document_type: Record<string, ProcessingTimeData>;
  trends: ProcessingTimeTrend[];
}

export interface ResponseTimeMetrics {
  average_ms: number;
  median_ms: number;
  p95_ms: number;
  p99_ms: number;
  max_ms: number;
}

export interface ThroughputMetrics {
  requests_per_second: number;
  requests_per_minute: number;
  requests_per_hour: number;
  peak_throughput: number;
}

export interface ErrorRateMetrics {
  overall_error_rate: number;
  error_4xx_rate: number;
  error_5xx_rate: number;
  error_count: number;
  total_requests: number;
}

export interface ResourceUsageMetrics {
  cpu_usage_percent: number;
  memory_usage_percent: number;
  disk_usage_percent: number;
  network_io_bytes: number;
}

export interface PerformanceTrend {
  date: string;
  response_time_ms: number;
  throughput_rps: number;
  error_rate_percent: number;
}

export interface PerformanceFilters {
  start_date?: string;
  end_date?: string;
  service?: string;
}

export interface SystemPerformance {
  response_time: ResponseTimeMetrics;
  throughput: ThroughputMetrics;
  error_rate: ErrorRateMetrics;
  resource_usage: ResourceUsageMetrics;
  performance_trends: PerformanceTrend[];
}

export interface ErrorTypeData {
  type: string;
  count: number;
  percentage: number;
}

export interface ErrorTrendData {
  date: string;
  error_count: number;
  error_rate: number;
}

export interface TopErrorData {
  error: string;
  count: number;
  last_seen: string;
}

export interface ServiceErrorData {
  service: string;
  error_count: number;
  error_rate: number;
}

export interface ErrorTrackingFilters {
  start_date?: string;
  end_date?: string;
  severity?: string;
  service?: string;
}

export interface ErrorTracking {
  total_errors: number;
  error_rate: number;
  error_types: ErrorTypeData[];
  error_trends: ErrorTrendData[];
  top_errors: TopErrorData[];
  service_errors: ServiceErrorData[];
}

export interface APIUsageData {
  endpoint: string;
  method: string;
  count: number;
  avg_response_time_ms: number;
}

export interface UserUsageData {
  user_id: string;
  username: string;
  request_count: number;
  last_active: string;
}

export interface ServiceUsageData {
  service: string;
  request_count: number;
  avg_response_time_ms: number;
  error_count: number;
}

export interface UsageTrendData {
  date: string;
  request_count: number;
  unique_users: number;
}

export interface UsageFilters {
  start_date?: string;
  end_date?: string;
  user_id?: string;
  service?: string;
}

export interface SystemUsage {
  total_requests: number;
  unique_users: number;
  api_usage: APIUsageData[];
  user_usage: UserUsageData[];
  service_usage: ServiceUsageData[];
  usage_trends: UsageTrendData[];
}

export interface CameraEventTypeData {
  type: string;
  count: number;
  percentage: number;
}

export interface ZoneDistributionData {
  zone_id: string;
  zone_name: string;
  camera_count: number;
  active_count: number;
}

export interface CameraPerformanceData {
  camera_id: string;
  camera_name: string;
  uptime_percent: number;
  event_count: number;
  avg_response_time_ms: number;
  last_seen: string;
}

export interface CameraEventTrendData {
  date: string;
  event_count: number;
  unique_cameras: number;
}

export interface CameraAnalyticsFilters {
  start_date?: string;
  end_date?: string;
  camera_id?: string;
  event_type?: string;
  zone_id?: string;
}

export interface CameraAnalytics {
  total_cameras: number;
  active_cameras: number;
  online_cameras: number;
  offline_cameras: number;
  event_count: number;
  event_types: CameraEventTypeData[];
  zone_distribution: ZoneDistributionData[];
  camera_performance: CameraPerformanceData[];
  event_trends: CameraEventTrendData[];
}

export interface CameraStatusOverview {
  total: number;
  online: number;
  offline: number;
  maintenance: number;
  error: number;
  disabled: number;
  status_distribution: Record<string, number>;
}

export interface EventTypeCount {
  type: string;
  count: number;
}

export interface EventTrendData {
  date: string;
  event_count: number;
}

export interface TopCameraData {
  camera_id: string;
  camera_name: string;
  event_count: number;
}

export interface EventSeverityData {
  severity: string;
  count: number;
}

export interface CameraEventFilters {
  start_date?: string;
  end_date?: string;
  camera_id?: string;
  event_type?: string;
  severity?: string;
}

export interface CameraEventMetrics {
  total_events: number;
  event_types: EventTypeCount[];
  event_trends: EventTrendData[];
  top_cameras: TopCameraData[];
  event_severity: EventSeverityData[];
}

export interface HealthComponent {
  name: string;
  status: string;
  score: number;
  message: string;
  last_checked: string;
}

export interface HealthAlert {
  id: string;
  type: string;
  severity: string;
  message: string;
  component: string;
  created_at: string;
  resolved_at?: string;
}

export interface SystemHealth {
  status: string;
  overall_score: number;
  components: HealthComponent[];
  alerts: HealthAlert[];
  last_checked: string;
  metrics: Record<string, unknown>;
}

export interface GeneralSettings {
  site_name: string;
  site_description: string;
  timezone: string;
  language: string;
  maintenance_mode: boolean;
  maintenance_message: string;
  contact_email: string;
  support_email: string;
}

export interface SecuritySettings {
  password_min_length: number;
  password_require_uppercase: boolean;
  password_require_lowercase: boolean;
  password_require_numbers: boolean;
  password_require_symbols: boolean;
  session_timeout: number;
  max_login_attempts: number;
  lockout_duration: number;
  two_factor_enabled: boolean;
  ip_whitelist: string[];
}

export interface KYCSettings {
  document_types: string[];
  max_file_size: number;
  allowed_file_types: string[];
  auto_approve_threshold: number;
  manual_review_required: boolean;
  retention_period: number;
  face_detection_enabled: boolean;
  liveness_detection_enabled: boolean;
}

export interface NotificationSettings {
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  email_smtp_host: string;
  email_smtp_port: number;
  email_smtp_username: string;
  email_smtp_password: string;
  email_from_address: string;
  sms_provider: string;
  sms_api_key: string;
  notification_templates: Record<string, string>;
}

export interface PerformanceSettings {
  cache_enabled: boolean;
  cache_ttl: number;
  max_upload_size: number;
  image_compression_quality: number;
  video_compression_quality: number;
  background_job_workers: number;
  database_pool_size: number;
  api_rate_limit: number;
}

export interface DeviceSettings {
  default_username: string;
  default_password: string;
  apply_to_unregistered: boolean;
  auto_scan_enabled: boolean;
  scan_interval_minutes: string;
  default_subnet: string;
  thumbnail_cache_minutes: string;
  stale_device_retention_days: string;
}

export interface SystemSettings {
  general: GeneralSettings;
  security: SecuritySettings;
  kyc: KYCSettings;
  notifications: NotificationSettings;
  performance: PerformanceSettings;
}

export interface SettingsValidationResponse {
  valid: boolean;
  errors: Array<{
    field: string;
    message: string;
  }>;
}

export interface SettingsAuditLog {
  id: string;
  category: string;
  key: string;
  old_value: unknown;
  new_value: unknown;
  changed_by: string;
  changed_at: string;
  ip_address: string;
  user_agent: string;
}

export interface SettingsAuditLogResponse {
  logs: SettingsAuditLog[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
}

// OTA types
export interface OTAPackage {
  id: string;
  version: string;
  file_name: string;
  file_size: number;
  checksum: string;
  description: string;
  uploaded_by: string;
  created_at: string;
}

export interface OTACampaign {
  id: string;
  package_id: string;
  name: string;
  target_sites: string[];
  target_agents: string[];
  batch_size: number;
  strategy: 'rolling' | 'canary' | 'immediate';
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'rolled_back';
  progress: number;
  created_at: string;
  updated_at: string;
}

export interface CampaignAgentStatus {
  campaign_id: string;
  agent_id: string;
  status: 'pending' | 'downloading' | 'installing' | 'success' | 'failed';
  progress: number;
  error: string;
  updated_at: string;
}

export interface CampaignStatus {
  campaign: OTACampaign;
  agents: CampaignAgentStatus[];
  summary: {
    total: number;
    pending: number;
    downloading: number;
    installing: number;
    success: number;
    failed: number;
  };
}

// Utility types
export type ApiResponse<T> = T;
export type QueryParams = Record<string, string | number | boolean | undefined>;

// Status enums
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

export enum KYCStatusEnum {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum DocumentType {
  PASSPORT = 'passport',
  DRIVERS_LICENSE = 'drivers_license',
  NATIONAL_ID = 'national_id',
}

// Site Management Types
export interface Site {
  id: string;
  name: string;
  description?: string;
  status: string;
  tenant_id?: string;
  created_at?: string;
  updated_at?: string;
  agent_count?: number;
}

export interface SiteListResponse {
  sites: Site[];
  total: number;
}
